import {Component, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from "@angular/material/dialog";

import {EcuNamesService} from "../../service/Ecu-names.service";
import {AbstractControl, FormControl, FormGroup, ValidationErrors} from "@angular/forms";
import {Subject} from "rxjs";
import {debounceTime, distinctUntilChanged, takeUntil} from "rxjs/operators";
import {EcuProducersService} from "../../service/ecu-producers.service";
import {EcuBuildNameModel} from "../../models/EcuBuildName-model";
import {HttpErrorResponse} from "@angular/common/http";

@Component({
    selector: 'app-add-dialog',
    templateUrl: './add-dialog.component.html',
    styleUrls: ['./add-dialog.component.css']
})
export class AddDialogComponent implements OnInit, OnDestroy {

    addNewEcuForm = new FormGroup(
        {
            id: new FormControl(0),
            correctEcuBuildNames: new FormControl(null, [this.requiredTrimmed, this.ecuBuildValidator.bind(this)]),
            connectedEcuBuildNames: new FormControl(null),
            acceptedEcuBuildNames: new FormControl(null, this.requiredTrimmed),
            correctEcuProducer: new FormControl(null, this.requiredTrimmed)
        }
    )

    ecuProducerOptions = signal<string[]>([]);
    private ecuProducerSearch$ = new Subject<string>();
    private destroy$ = new Subject<void>();
    isSaving = false;
    saveError = '';

    constructor(
        private ecuService: EcuNamesService,
        private ecuProducersService: EcuProducersService,
        public dialogRef: MatDialogRef<AddDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: any
    ) {}

    ngOnInit() {
        this.initEcuProducerAutocomplete();

        const ev = this.data?.ecuBuildValue;
        if (ev != null) {
            if (typeof ev === 'string') {
                this.addNewEcuForm.patchValue({
                    correctEcuBuildNames: ev,
                    connectedEcuBuildNames: null
                });
            } else {
                this.addNewEcuForm.patchValue({
                    correctEcuBuildNames: ev.correctEcuBuildNames ?? null,
                    connectedEcuBuildNames: ev.connectedEcuBuildNames ?? null
                });
            }
        }
        this.addNewEcuForm.valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => this.saveError = '');
        this.ecuProducerSearch$.next(this.addNewEcuForm.get('correctEcuProducer')?.value ?? '');
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private initEcuProducerAutocomplete() {
        this.ecuProducersService.getAllCorrectEcuProducers();

        this.ecuProducerSearch$
            .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(searchTerm => {
                const all = this.ecuProducersService.correctEcuProducersList() || [];
                const trimmed = (searchTerm || '').trim().toLowerCase();
                this.ecuProducerOptions.set(
                    trimmed
                        ? all.filter(item => item.toLowerCase().includes(trimmed))
                        : all
                );
            });

        this.addNewEcuForm.get('correctEcuProducer').valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe(value => this.ecuProducerSearch$.next(value ?? ''));
    }

    private requiredTrimmed(control: AbstractControl): ValidationErrors | null {
        return typeof control.value === 'string' && control.value.trim() ? null : {required: true};
    }

    ecuBuildValidator(control: AbstractControl): ValidationErrors | null {
        return this.ecuService.checkDuplicateEcuBuildName(control.value) ? {ecuBuildExists: true} : null;
    }

    confirmedOption(ecu: EcuBuildNameModel) {
        this.dialogRef.close({closed: true, ecu: ecu});
    }

    canceledOption() {
        this.dialogRef.close({closed: false, ecuName: null});
    }

    saveNewRow() {
        if (this.isSaving || this.addNewEcuForm.invalid) {
            this.addNewEcuForm.markAllAsTouched();
            return;
        }

        this.saveError = '';
        const newRow: EcuBuildNameModel = {
            id: 0,
            correctEcuBuildNames: this.addNewEcuForm.get('correctEcuBuildNames')?.value.trim(),
            connectedEcuBuildNames: this.addNewEcuForm.get('connectedEcuBuildNames')?.value?.trim() || null,
            acceptedEcuBuildNames: this.addNewEcuForm.get('acceptedEcuBuildNames')?.value.trim(),
            correctEcuProducer: this.addNewEcuForm.get('correctEcuProducer')?.value.trim()
        };

        this.isSaving = true;
        this.ecuService.createEcuBuildItem(newRow).pipe(takeUntil(this.destroy$)).subscribe({
            next: savedEcu => {
                this.isSaving = false;
                this.confirmedOption(savedEcu);
            },
            error: (error: HttpErrorResponse) => {
                this.isSaving = false;
                const fieldErrors = error.error?.errors;
                this.saveError = error.error?.message ||
                    (fieldErrors ? Object.values(fieldErrors).join(' ') : '') ||
                    'Could not save the ECU Build. Please try again.';
            }
        });
    }
}
